import type { DisplayCurrency, FxRates } from './types'
import { FX_CACHE_KEY } from './types'

const FRANKFURTER_URL = 'https://api.frankfurter.app/latest?from=USD&to=AUD,GBP,EUR'

/** Fallback rates if API unavailable (approximate daily). */
const FALLBACK_RATES: FxRates['rates'] = {
  AUD: 1.55,
  GBP: 0.79,
  EUR: 0.92,
}

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10)
}

function readCache(): FxRates | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(FX_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as FxRates
    if (parsed?.base !== 'USD' || !parsed.rates) return null
    if (parsed.fetchedAt !== todayUtc()) return null
    return parsed
  } catch {
    return null
  }
}

function writeCache(rates: FxRates): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(FX_CACHE_KEY, JSON.stringify(rates))
  } catch {
    /* quota */
  }
}

export async function fetchFxRates(): Promise<FxRates> {
  const cached = readCache()
  if (cached) return cached

  try {
    const res = await fetch(FRANKFURTER_URL, { signal: AbortSignal.timeout(8000) })
    if (!res.ok) throw new Error(`FX ${res.status}`)
    const data = (await res.json()) as { rates?: Record<string, number> }
    const rates: FxRates['rates'] = {
      AUD: Number(data.rates?.AUD) || FALLBACK_RATES.AUD,
      GBP: Number(data.rates?.GBP) || FALLBACK_RATES.GBP,
      EUR: Number(data.rates?.EUR) || FALLBACK_RATES.EUR,
    }
    const payload: FxRates = { base: 'USD', fetchedAt: todayUtc(), rates }
    writeCache(payload)
    return payload
  } catch {
    return { base: 'USD', fetchedAt: todayUtc(), rates: { ...FALLBACK_RATES } }
  }
}

export function rateFor(currency: DisplayCurrency, fx: FxRates | null): number {
  if (currency === 'USD') return 1
  if (!fx) return FALLBACK_RATES[currency]
  return fx.rates[currency] ?? FALLBACK_RATES[currency]
}
